import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CATALOGUES, DEFAULT_LANGUAGE, LANGUAGES } from './languages';

// Deliberately dependency-free.
//
// react-i18next would be the obvious choice, but it brings plural rules,
// namespaces and a loader we do not need yet. This is the same contract in
// ~90 lines: t(key), language, setLanguage. If interpolation or plurals are
// ever needed, swap the provider for i18next and keep every call site as it is.
//
// WHERE THE CHOICE LIVES
// ----------------------
// The company owns the language (CompanyMaster.Language), not the browser.
// Every terminal in the property therefore reads the same screens, and a user
// moving to another PC keeps their language instead of getting English again.
// localStorage is only a MIRROR: it lets the first paint after a reload use the
// right language without waiting for the login payload to arrive.

const STORAGE_KEY = 'appLanguage';

const isSupported = (c) => LANGUAGES.some((l) => l.code === c);

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  languages: LANGUAGES,
  t: (key) => key,
});

// Company first (authoritative), then the local mirror, then English.
const readInitial = () => {
  try {
    const company = JSON.parse(sessionStorage.getItem('companyDetails') || 'null');
    const fromCompany = company?.language || company?.Language;
    if (isSupported(fromCompany)) return fromCompany;

    const mirror = localStorage.getItem(STORAGE_KEY);
    if (isSupported(mirror)) return mirror;
  } catch {
    /* fall through to the default */
  }
  return DEFAULT_LANGUAGE;
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readInitial);

  // Applies the choice locally. Persisting it to the company is the settings
  // screen's job, because only that screen knows whether the save succeeded.
  const setLanguage = useCallback((code) => {
    if (!isSupported(code)) return;
    try {
      localStorage.setItem(STORAGE_KEY, code);

      // Keep the cached company row in step so a reload before the next login
      // does not silently revert the language.
      const raw = sessionStorage.getItem('companyDetails');
      if (raw) {
        const company = JSON.parse(raw);
        company.language = code;
        if ('Language' in company) company.Language = code;
        sessionStorage.setItem('companyDetails', JSON.stringify(company));
      }
    } catch {
      /* private mode / quota - the in-memory choice still applies */
    }
    setLanguageState(code);
  }, []);

  // A missing key falls back to English, then to the key itself. That means a
  // half-translated language shows English words rather than 'nav.reception',
  // so shipping a partial catalogue is safe.
  const t = useCallback(
    (key, fallback) => {
      const active = CATALOGUES[language] || {};
      return active[key] ?? CATALOGUES.en[key] ?? fallback ?? key;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, languages: LANGUAGES, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);

// Convenience for components that only need the translate function.
export const useT = () => useContext(LanguageContext).t;